import { analytics } from "../analytics/index.js";
import type { TournamentDto } from "../api/dto/tournament.dto.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";

const STORE_KEY = "tournament_analytics_v1";
const MAX_REMEMBERED = 40;

interface ReportedState
{
  started: string[];
  steps: Record<string, number>;
}

function load(): ReportedState
{
  const stored = PersistentCache.read<ReportedState>( STORE_KEY );
  return {
    started: Array.isArray( stored?.started ) ? stored!.started : [],
    steps: stored?.steps && typeof stored.steps === "object" ? stored.steps : {},
  };
}

function save( state: ReportedState ): void
{
  const started = state.started.slice( -MAX_REMEMBERED );
  const keep = new Set( started );
  const steps = Object.fromEntries( Object.entries( state.steps ).filter( ( [ key ] ) => keep.has( key ) ) );
  PersistentCache.write( STORE_KEY, { started, steps } );
}

function hash( text: string ): string
{
  let a = 0x811c9dc5, b = 0x9747b28c;
  for ( let i = 0; i < text.length; i++ )
  {
    const code = text.charCodeAt( i );
    a = Math.imul( a ^ code, 0x01000193 ) >>> 0;
    b = Math.imul( b ^ code, 0x5bd1e995 ) >>> 0;
  }
  return a.toString( 16 ).padStart( 8, "0" ) + b.toString( 16 ).padStart( 8, "0" );
}

function athenaDateTime( iso: string ): string
{
  const date = new Date( iso );
  return Number.isNaN( date.getTime() ) ? "" : date.toISOString().slice( 0, 19 ).replace( "T", " " );
}

function commonParams( tournament: TournamentDto, event_name: string, subject: string ): Record<string, unknown>
{
  return {
    sourceId: event_name,
    event_id: tournament.instance,
    config_name: tournament.id,
    room_id: tournament.room_id ?? null,
    is_bot: false,
    req_trans_id: hash( [ event_name, tournament.instance, subject ].join( "|" ) ),
    date_start: athenaDateTime( tournament.starts_at ),
    date_end: athenaDateTime( tournament.ends_at ),
    segment_name: tournament.segment_name ?? "",
  };
}

function balanceParams(): Record<string, unknown>
{
  const stats = StatisticsStorage.load();
  return {
    balance_coins: stats.coins,
    balance_hearts: stats.hearts,
    balance_big_balls: stats.big_balls,
    balance_infinite_balls: stats.infinite_balls,
    balance_multi_balls: stats.multiballs,
    balance_fireworks: stats.fireworks,
  };
}

/**
 * Turns server tournament state into the analytics events of the tournament
 * spec: tournament_started once per launch, tournament_event_step once per
 * checkpoint the server-confirmed points have reached. tournament_completed is
 * sent by the server when the player returns after the end.
 */
export const TournamentAnalytics = {
  observe( tournament: TournamentDto | null | undefined ): void
  {
    // Before the first win the cached payload has no room yet; the win report or the
    // refetch after it carries one, so wait for that instead of sending room_id null.
    if ( !tournament?.room_id ) return;
    const state = load();
    let changed = false;

    if ( tournament.joined && !state.started.includes( tournament.instance ) )
    {
      analytics.track( "tournament_started", { ...commonParams( tournament, "tournament_started", "start" ), ...balanceParams() } );
      state.started.push( tournament.instance );
      changed = true;
    }

    const reported = state.steps[tournament.instance] ?? 0;
    const total = tournament.progress_rewards.length;
    const reached = tournament.progress_rewards.filter( row => tournament.crowns >= row.crowns ).length;
    for ( let step = reported + 1; step <= reached; step++ )
    {
      analytics.track( "tournament_event_step", {
        ...commonParams( tournament, "tournament_event_step", `step:${step}` ),
        step,
        steps_total: total,
        total_points: tournament.crowns,
        ...balanceParams(),
      } );
    }
    if ( reached > reported )
    {
      state.steps[tournament.instance] = reached;
      changed = true;
    }

    if ( changed ) save( state );
  },
};
